import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Brain, Swords, TrendingDown, Sparkles, Flame, ArrowRight, Share2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TOOL_META = {
  quiz: { icon: Brain, color: '#4d7cfe', cta: 'Take the Quiz', path: '/viral-tools/gambler-personality-quiz' },
  gambler_personality: { icon: Brain, color: '#4d7cfe', cta: 'Take the Quiz', path: '/viral-tools/gambler-personality-quiz' },
  challenge: { icon: Swords, color: '#8b5cf6', cta: 'Take the Challenge', path: '/viral-tools/friend-challenge' },
  friend_challenge: { icon: Swords, color: '#8b5cf6', cta: 'Take the Challenge', path: '/viral-tools/friend-challenge' },
  win_prediction: { icon: TrendingDown, color: '#ef4444', cta: 'Get Your Reality Check', path: '/viral-tools/win-prediction-simulator' },
  slot_matcher: { icon: Sparkles, color: '#10b981', cta: 'Find Your Match', path: '/viral-tools/slot-volatility-matcher' },
  streak: { icon: Flame, color: '#f59e0b', cta: 'Track Your Streak', path: '/viral-tools/betting-streak-tracker' },
  win_celebration: { icon: Trophy, color: '#f59e0b', cta: 'Celebrate Your Wins', path: '/emotional-wellness/win-celebration' },
};

export default function ShareResult() {
  const { type, id } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/api/tools/share-card/${id}`).then(r => setCard(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen bg-[#0f1219] flex items-center justify-center"><p className="text-[#64748b]">Loading...</p></div>;
  if (!card) return (
    <div className="min-h-screen bg-[#0f1219] flex flex-col items-center justify-center px-4">
      <p className="text-white text-lg font-bold mb-2">Share link not found</p>
      <Link to="/" className="text-[#4d7cfe] text-sm hover:underline">Go to CasinoIQ</Link>
    </div>
  );

  const meta = TOOL_META[type] || TOOL_META.quiz;
  const Icon = meta.icon;
  const data = card.result_data || {};

  return (
    <div className="min-h-screen bg-[#0f1219] flex flex-col items-center justify-center px-4 py-12" data-testid="share-result-page">
      {/* Header */}
      <Link to="/" className="text-xl font-bold text-white tracking-tight mb-8">
        Casino<span className="text-[#4d7cfe]">IQ</span>
      </Link>

      {/* Result Card */}
      <div className="w-full max-w-md ciq-card text-center animate-slide-up" style={{ borderColor: `${meta.color}30` }}>
        <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${meta.color}15` }}>
          <Icon className="h-8 w-8" style={{ color: meta.color }} />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2" data-testid="share-title">{card.title}</h1>
        <p className="text-[#a0a8b8] text-sm mb-6">{card.description}</p>

        {/* Tool-specific result display */}
        {(type === 'quiz' || type === 'gambler_personality') && data.archetype_name && (
          <div className="p-4 rounded-xl bg-[#1a1f2e] mb-4">
            <p className="text-xs text-[#64748b] uppercase tracking-wider mb-1">Gambler Personality</p>
            <p className="text-xl font-bold" style={{ color: meta.color }}>{data.archetype_name}</p>
            {data.percentage && <p className="text-xs text-[#a0a8b8] mt-1">Only {data.percentage}% of players share this type</p>}
            {data.share_text && <p className="text-sm text-[#a0a8b8] mt-2 italic">"{data.share_text}"</p>}
          </div>
        )}
        {(type === 'challenge' || type === 'friend_challenge') && (
          <div className="p-4 rounded-xl bg-[#1a1f2e] mb-4">
            <p className="text-xs text-[#64748b] uppercase tracking-wider mb-1">Score</p>
            <p className="text-3xl font-bold" style={{ color: meta.color }}>{data.score}<span className="text-lg text-[#64748b]">/100</span></p>
          </div>
        )}
        {type === 'win_celebration' && (
          <div className="p-4 rounded-xl bg-[#1a1f2e] mb-4">
            <p className="text-xs text-[#64748b] uppercase tracking-wider mb-1">Win Amount</p>
            <p className="text-3xl font-bold text-[#f59e0b]">${data.win_amount?.toLocaleString()}</p>
            {data.game_type && <p className="text-xs text-[#a0a8b8] mt-1">on {data.game_type}</p>}
          </div>
        )}
        {type === 'slot_matcher' && data.profile && (
          <div className="p-4 rounded-xl bg-[#1a1f2e] mb-4">
            <p className="text-xs text-[#64748b] uppercase tracking-wider mb-1">Volatility Profile</p>
            <p className="text-lg font-bold" style={{ color: meta.color }}>{data.profile}</p>
          </div>
        )}
        {type === 'streak' && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Sessions</p><p className="text-lg font-bold text-white">{data.total_sessions}</p></div>
            <div className="p-3 rounded-lg bg-[#1a1f2e]"><p className="text-xs text-[#64748b]">Win Rate</p><p className="text-lg font-bold text-[#10b981]">{data.win_rate}%</p></div>
          </div>
        )}
        {type === 'win_prediction' && (
          <div className="p-4 rounded-xl bg-[#1a1f2e] mb-4">
            <p className="text-xs text-[#64748b] uppercase tracking-wider mb-1">Gambling Reality Check</p>
            <p className="text-sm text-[#a0a8b8]">${data.monthly_budget}/mo budget analyzed</p>
          </div>
        )}

        {/* CTA */}
        <Link to={meta.path} className="w-full h-12 rounded-xl bg-[#4d7cfe] text-white font-medium text-sm btn-lift flex items-center justify-center gap-2 mb-3" data-testid="share-cta-btn">
          {meta.cta} <ArrowRight className="h-4 w-4" />
        </Link>
        <p className="text-[10px] text-[#64748b]">Powered by CasinoIQ - Smart Tools for Smart Players</p>
      </div>

      {/* Footer */}
      <p className="mt-8 text-xs text-[#64748b]">18+ | Play Responsibly</p>
    </div>
  );
}
